/* ============================================================
   SAM-590 — Add-Ons editor, mounted in PoliciesSection.tsx in place of the
   disabled placeholder. Add-ons are OPTIONAL charge lines that never affect
   capacity/schedule/provider (Rodrigo's rule: if it did, it would be a
   separate service instead) — see useServiceEditor.ts's AddOnForm and
   formToServiceData's settings.add_ons block for the write side.

   Rows: id (slug, auto-derived from label_en, editable), label EN/ES, price,
   currency (defaults to the service's first booking option currency —
   addAddOn in useServiceEditor.ts), max qty, required toggle, and an
   optional per-role price popover (roles from /user_type, same source as
   ServicePricingMatrix — but a full matrix would be overkill for a handful
   of add-on rows, hence the small popover instead of reusing that component
   wholesale).
   ============================================================ */
import React, { useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Tag, DollarSign, X } from 'lucide-react';
import { useUserTypes } from '../../../hooks/useUserTypes';
import { CURRENCY_OPTIONS, type AddOnForm } from '../../../hooks/useServiceEditor';
import type { Editor } from '../ServiceEditorPage';

export function AddOnsEditor({ editor }: { editor: Editor }) {
  const {
    formData, addAddOn, removeAddOn, updateAddOn, reorderAddOn, updateAddOnRolePrice, addOnErrors,
  } = editor;
  const [rolePopoverIndex, setRolePopoverIndex] = useState<number | null>(null);

  const addOns = formData.add_ons;

  return (
    <div>
      {addOns.length === 0 && (
        <p className="text-[12px] mb-3" style={{ color: 'var(--ink-500)' }}>
          No add-ons yet. Add-ons are optional charge lines a guest can pick when booking this
          service (cart, caddie, towel, locker, …) — they never affect capacity or schedule.
        </p>
      )}

      <div className="space-y-3">
        {addOns.map((addOn, index) => {
          const errors = addOnErrors[index] || [];
          return (
            <AddOnRow
              key={index}
              addOn={addOn}
              index={index}
              isFirst={index === 0}
              isLast={index === addOns.length - 1}
              errors={errors}
              onChange={(field, value) => updateAddOn(index, field, value)}
              onRemove={() => removeAddOn(index)}
              onMoveUp={() => reorderAddOn(index, -1)}
              onMoveDown={() => reorderAddOn(index, 1)}
              onTogglePricePerRole={() => setRolePopoverIndex(rolePopoverIndex === index ? null : index)}
              rolePopoverOpen={rolePopoverIndex === index}
              onRolePriceChange={(userTypeId, value) => updateAddOnRolePrice(index, userTypeId, value)}
            />
          );
        })}
      </div>

      <button type="button" className="btn ghost mt-3" style={{ width: '100%' }} onClick={addAddOn}>
        <Plus size={14} /> Add add-on
      </button>
    </div>
  );
}

function AddOnRow({
  addOn, index, isFirst, isLast, errors, onChange, onRemove, onMoveUp, onMoveDown,
  onTogglePricePerRole, rolePopoverOpen, onRolePriceChange,
}: {
  addOn: AddOnForm;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  errors: string[];
  onChange: (field: keyof AddOnForm, value: string | boolean) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onTogglePricePerRole: () => void;
  rolePopoverOpen: boolean;
  onRolePriceChange: (userTypeId: string, value: string) => void;
}) {
  const { userTypes } = useUserTypes();
  const roleCount = Object.keys(addOn.prices_by_role || {}).length;

  return (
    <div
      className="rounded-8 p-3"
      style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-8)', background: 'var(--surface-2)' }}
      data-testid={`addon-row-${index}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1 pt-1">
          <button
            type="button"
            aria-label="Move up"
            disabled={isFirst}
            onClick={onMoveUp}
            className="rounded-full flex items-center justify-center"
            style={{ width: 20, height: 20, border: '1px solid var(--line)', background: '#fff', opacity: isFirst ? 0.4 : 1 }}
          >
            <ArrowUp size={11} />
          </button>
          <button
            type="button"
            aria-label="Move down"
            disabled={isLast}
            onClick={onMoveDown}
            className="rounded-full flex items-center justify-center"
            style={{ width: 20, height: 20, border: '1px solid var(--line)', background: '#fff', opacity: isLast ? 0.4 : 1 }}
          >
            <ArrowDown size={11} />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <div className="fc">
              <label>Label (EN)</label>
              <input
                value={addOn.label_en}
                onChange={(e) => onChange('label_en', e.target.value)}
                placeholder="e.g. Golf cart"
                aria-label={`Add-on ${index + 1} label EN`}
              />
            </div>
            <div className="fc">
              <label>Label (ES)</label>
              <input
                value={addOn.label_es}
                onChange={(e) => onChange('label_es', e.target.value)}
                placeholder="p. ej. Carrito de golf"
                aria-label={`Add-on ${index + 1} label ES`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2 mt-2">
            <div className="fc">
              <label>Id</label>
              <input
                value={addOn.id}
                onChange={(e) => onChange('id', e.target.value)}
                placeholder="auto"
                aria-label={`Add-on ${index + 1} id`}
              />
            </div>
            <div className="fc">
              <label>Price</label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={addOn.price}
                onChange={(e) => onChange('price', e.target.value)}
                aria-label={`Add-on ${index + 1} price`}
              />
            </div>
            <div className="fc">
              <label>Currency</label>
              <select
                value={addOn.currency}
                onChange={(e) => onChange('currency', e.target.value)}
                aria-label={`Add-on ${index + 1} currency`}
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="fc">
              <label>Max qty</label>
              <input
                type="number"
                min={1}
                value={addOn.max_qty}
                onChange={(e) => onChange('max_qty', e.target.value)}
                aria-label={`Add-on ${index + 1} max qty`}
              />
            </div>
          </div>

          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <label className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--ink-700)' }}>
              <input
                type="checkbox"
                checked={addOn.required}
                onChange={(e) => onChange('required', e.target.checked)}
              />
              Required (guest always gets at least 1)
            </label>

            <div style={{ position: 'relative' }}>
              <button type="button" className="btn ghost" onClick={onTogglePricePerRole}>
                <DollarSign size={12} />
                {roleCount > 0 ? `Price per role (${roleCount})` : 'Price per role'}
              </button>
              {rolePopoverOpen && (
                <RolePricePopover
                  userTypes={userTypes}
                  pricesByRole={addOn.prices_by_role}
                  flatPrice={addOn.price}
                  currency={addOn.currency}
                  onChange={onRolePriceChange}
                  onClose={onTogglePricePerRole}
                />
              )}
            </div>
          </div>

          {errors.length > 0 && (
            <ul className="text-[11px] mt-2 list-disc pl-4" style={{ color: 'var(--danger)' }}>
              {errors.map((err) => <li key={err}>{err}</li>)}
            </ul>
          )}
        </div>

        <button
          type="button"
          aria-label={`Remove add-on ${index + 1}`}
          onClick={onRemove}
          className="rounded-full flex items-center justify-center flex-shrink-0"
          style={{ width: 24, height: 24, border: '1px solid var(--line)', background: '#fff' }}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function RolePricePopover({
  userTypes, pricesByRole, flatPrice, currency, onChange, onClose,
}: {
  userTypes: Array<{ id: number | string; name: string }>;
  pricesByRole: Record<string, string>;
  flatPrice: string;
  currency: string;
  onChange: (userTypeId: string, value: string) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="rounded-8 p-3"
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 4,
        width: 260,
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-8)',
        background: '#fff',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        zIndex: 20000,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-semibold flex items-center gap-1" style={{ color: 'var(--ink-900)' }}>
          <Tag size={12} /> Price per role
        </span>
        <button type="button" aria-label="Close" onClick={onClose}>
          <X size={14} />
        </button>
      </div>
      <p className="text-[11px] mb-2" style={{ color: 'var(--ink-400)' }}>
        Blank uses the flat price ({flatPrice || '0'} {currency}).
      </p>
      <div className="space-y-1.5">
        {userTypes.map((ut) => (
          <div key={String(ut.id)} className="flex items-center justify-between gap-2">
            <span className="text-[12px] truncate" style={{ color: 'var(--ink-700)' }}>{ut.name}</span>
            <input
              type="number"
              min={0}
              step="0.01"
              style={{ width: 80 }}
              value={pricesByRole[String(ut.id)] ?? ''}
              onChange={(e) => onChange(String(ut.id), e.target.value)}
              aria-label={`Price for ${ut.name}`}
            />
          </div>
        ))}
        {userTypes.length === 0 && (
          <p className="text-[11px]" style={{ color: 'var(--ink-400)' }}>No roles configured yet.</p>
        )}
      </div>
    </div>
  );
}
